import React, { useState, useMemo, useEffect, useRef, useCallback, memo } from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription,
  DialogHeader, 
  DialogTitle 
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import {
  Download,
  X,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertCircle,
  CheckCircle,
  Clock,
  Target,
  Lightbulb,
  Send,
  MessageCircle
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { IADSustento } from './iad/IADSustento';
import { IADSelector } from './iad/IADSelector';

const API = process.env.REACT_APP_BACKEND_URL || '';

// COLORS for charts - defined outside component to prevent re-creation
const CHART_COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// ChartComponent - defined outside to prevent re-creation on every render
const ChartComponent = memo(({ chartData }) => {
  if (!chartData || !chartData.type || !chartData.data) return null;

  const { type, title, data } = chartData;

  // Ensure data has correct format
  const normalizedData = data.map(item => ({
    name: item.name || item.label || item.state || 'N/A',
    value: typeof item.value === 'number' ? item.value : parseFloat(item.value) || 0
  }));

  switch (type) {
    case 'line':
      return (
        <div className="my-4 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
          <h4 className="text-sm font-semibold text-slate-200 mb-3">{title}</h4>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={normalizedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Line type="monotone" dataKey="value" stroke="#3b82f6" strokeWidth={2} dot={{ fill: '#3b82f6' }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      );
    case 'bar':
      return (
        <div className="my-4 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
          <h4 className="text-sm font-semibold text-slate-200 mb-3">{title}</h4>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={normalizedData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
              <XAxis dataKey="name" stroke="#94a3b8" fontSize={12} />
              <YAxis stroke="#94a3b8" fontSize={12} />
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
                labelStyle={{ color: '#e2e8f0' }}
              />
              <Bar dataKey="value" fill="#10b981" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      );
    case 'pie':
      return (
        <div className="my-4 bg-slate-800/50 p-4 rounded-lg border border-slate-700">
          <h4 className="text-sm font-semibold text-slate-200 mb-3">{title}</h4>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={normalizedData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
              >
                {normalizedData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                ))}
              </Pie>
              <Tooltip 
                contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155', borderRadius: '8px' }}
              />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      );
    default:
      return null;
  }
});

ChartComponent.displayName = 'ChartComponent';

const ReportViewerModal = ({ thread, open, onClose }) => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [conversationHistory, setConversationHistory] = useState([]);
  const [followUpMessage, setFollowUpMessage] = useState('');
  const [followUpIads, setFollowUpIads] = useState([]);
  const [sendingMessage, setSendingMessage] = useState(false);

  // Helper function to clean HTML from markdown code blocks
  const cleanHtmlFromCodeBlocks = (str) => {
    if (!str) return '';
    let cleaned = str.trim();
    
    // Remove ```html ... ``` wrapper (handle various formats)
    cleaned = cleaned.replace(/^```html\s*/i, '').replace(/\s*```$/g, '');
    cleaned = cleaned.replace(/^```\s*/i, '').replace(/\s*```$/g, '');
    
    // Also handle cases where there might be multiple code blocks
    cleaned = cleaned.replace(/```html\s*/gi, '').replace(/```/g, '');
    
    return cleaned.trim();
  };

  // Helper function to check if content is HTML
  const isHTML = (str) => {
    if (!str) return false;
    
    // First, clean any markdown code block wrappers
    const cleaned = cleanHtmlFromCodeBlocks(str).trim();
    
    // Check for HTML signatures - more comprehensive check
    const htmlPatterns = [
      /^<!DOCTYPE/i,
      /^<html/i,
      /^<div\s*/i,
      /^<div>/i,
      /^<body/i,
      /^<h[1-6]/i,
      /^<p\s*/i,
      /^<p>/i,
      /^<table/i,
      /^<section/i,
      /^<article/i,
      /^<header/i,
      /^<main/i,
    ];
    
    // Check if starts with any HTML pattern
    for (const pattern of htmlPatterns) {
      if (pattern.test(cleaned)) {
        return true;
      }
    }
    
    // Check for significant HTML structure (tags with attributes)
    if (/<[a-z]+\s+[^>]*style\s*=/i.test(cleaned)) {
      return true;
    }
    
    // Check for substantial HTML content
    if ((/<(div|body|html|head|style|table|section)\s/i.test(cleaned) && /<\/(div|body|html|table|section)>/i.test(cleaned))) {
      return true;
    }
    
    // Check if content contains DOCTYPE or html tag anywhere (not just at start)
    if (/<!DOCTYPE\s+html/i.test(cleaned) || /<html[^>]*>/i.test(cleaned)) {
      return true;
    }
    
    // Check for style tags with CSS (common in agent outputs)
    if (/<style[^>]*>[\s\S]*?<\/style>/i.test(cleaned)) {
      return true;
    }
    
    return false;
  };

  // Helper function to extract body content from full HTML
  const extractBodyContent = (html) => {
    if (!html) return '';
    
    // First clean markdown code blocks
    let cleaned = cleanHtmlFromCodeBlocks(html);
    
    // If it's a full HTML document with styles, extract them and body
    if (cleaned.includes('<!DOCTYPE') || cleaned.includes('<html')) {
      // Extract styles from head
      const styleMatch = cleaned.match(/<style[^>]*>([\s\S]*?)<\/style>/gi);
      let styles = '';
      if (styleMatch) {
        styles = styleMatch.join('\n');
      }
      
      // Extract body content
      const bodyMatch = cleaned.match(/<body[^>]*>([\s\S]*)<\/body>/i);
      if (bodyMatch) {
        return styles + bodyMatch[1];
      }
      
      // No body tag, try to get content after </head>
      const afterHead = cleaned.split(/<\/head>/i)[1];
      if (afterHead) {
        return styles + afterHead.replace(/<\/?html[^>]*>/gi, '');
      }
    }
    
    // Otherwise return cleaned content
    return cleaned;
  };

  // ChartComponent is now defined outside the component to prevent re-creation

  // Extract chart JSON objects from HTML content
  const extractChartsFromHtml = (html) => {
    if (!html) return { cleanedHtml: html, charts: [] };
    
    const charts = [];
    let content = html;
    
    // Pattern to find chart JSON objects - more flexible
    const chartPatterns = [
      /\{\s*"type"\s*:\s*"(bar|line|pie)"[\s\S]*?"data"\s*:\s*\[[\s\S]*?\]\s*\}/gi,
      /\{\s*"data"\s*:\s*\[[\s\S]*?\][\s\S]*?"type"\s*:\s*"(bar|line|pie)"[\s\S]*?\}/gi
    ];
    
    for (const regex of chartPatterns) {
      let match;
      while ((match = regex.exec(content)) !== null) {
        try {
          // Clean up the match - remove any trailing characters that might break parsing
          let jsonStr = match[0];
          // Balance the braces
          let braceCount = 0;
          let endIndex = 0;
          for (let i = 0; i < jsonStr.length; i++) {
            if (jsonStr[i] === '{') braceCount++;
            if (jsonStr[i] === '}') braceCount--;
            if (braceCount === 0) {
              endIndex = i + 1;
              break;
            }
          }
          jsonStr = jsonStr.substring(0, endIndex);
          
          const chartData = JSON.parse(jsonStr);
          if (chartData.type && chartData.data && Array.isArray(chartData.data)) {
            charts.push(chartData);
          }
        } catch (e) {
          console.log('[extractChartsFromHtml] Failed to parse chart:', e.message);
        }
      }
    }
    
    return { cleanedHtml: content, charts };
  };

  // Component to render full HTML documents with chart support
  const IframeHtmlRenderer = ({ html }) => {
    const iframeRef = useRef(null);
    const [extractedCharts, setExtractedCharts] = useState([]);
    
    // Process HTML to extract actual content from various wrapper formats
    const processHtml = React.useCallback((rawHtml) => {
      if (!rawHtml) return '';
      
      let content = rawHtml;
      
      // 1. Remove markdown code block wrappers (```html ... ```)
      content = content.replace(/^```html\s*/i, '').replace(/\s*```$/g, '');
      content = content.replace(/^```\s*/i, '').replace(/\s*```$/g, '');
      
      // 2. Check if HTML contains escaped HTML inside <pre><code>
      const codeBlockMatch = content.match(/<pre><code[^>]*>([\s\S]*?)<\/code><\/pre>/i);
      if (codeBlockMatch) {
        let escapedContent = codeBlockMatch[1];
        const textarea = document.createElement('textarea');
        textarea.innerHTML = escapedContent;
        const decodedHtml = textarea.value;
        
        if (decodedHtml.trim().startsWith('<!DOCTYPE') || decodedHtml.trim().startsWith('<html') || decodedHtml.trim().startsWith('<div')) {
          content = decodedHtml;
        }
      }
      
      // 3. Check for escaped entities
      if (content.includes('&amp;lt;') || content.includes('&amp;gt;')) {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = content;
        content = textarea.value;
        if (content.includes('&lt;') || content.includes('&gt;')) {
          textarea.innerHTML = content;
          content = textarea.value;
        }
      }
      
      if (content.includes('&lt;!DOCTYPE') || content.includes('&lt;html') || content.includes('&lt;div')) {
        const textarea = document.createElement('textarea');
        textarea.innerHTML = content;
        content = textarea.value;
      }
      
      // 4. If content is not a full HTML document, wrap it
      const trimmed = content.trim();
      if (!trimmed.startsWith('<!DOCTYPE') && !trimmed.toLowerCase().startsWith('<html')) {
        content = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <style>
    body { 
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      margin: 0;
      padding: 16px;
      background: white;
      color: #1f2937;
      line-height: 1.6;
    }
    table { 
      border-collapse: collapse; 
      width: 100%; 
      margin: 16px 0;
    }
    th, td { 
      border: 1px solid #e5e7eb; 
      padding: 12px; 
      text-align: left; 
    }
    th { 
      background: #f3f4f6; 
      font-weight: 600;
    }
    tr:nth-child(even) { background: #f9fafb; }
    h1, h2, h3, h4 { color: #111827; margin-top: 24px; margin-bottom: 12px; }
    h1 { font-size: 24px; }
    h2 { font-size: 20px; }
    h3 { font-size: 18px; }
    p { margin: 12px 0; }
    ul, ol { margin: 12px 0; padding-left: 24px; }
    li { margin: 6px 0; }
    .metric { 
      display: inline-block; 
      padding: 8px 16px; 
      margin: 4px; 
      border-radius: 8px; 
      font-weight: 600;
    }
    .positive { background: #d1fae5; color: #065f46; }
    .negative { background: #fee2e2; color: #991b1b; }
    .neutral { background: #e0f2fe; color: #075985; }
  </style>
</head>
<body>
${content}
</body>
</html>`;
      }
      
      return content;
    }, []);
    
    useEffect(() => {
      if (html) {
        // First process HTML
        let processedHtml = processHtml(html);
        
        // Extract charts from processed HTML
        const { charts } = extractChartsFromHtml(processedHtml);
        if (charts.length > 0) {
          setExtractedCharts(charts);
          
          // Remove chart JSON blocks from HTML to avoid showing them as text
          // Pattern 1: Remove JSON objects that look like charts
          processedHtml = processedHtml.replace(
            /\{\s*"type"\s*:\s*"(bar|line|pie)"[\s\S]*?"data"\s*:\s*\[[\s\S]*?\]\s*\}/gi,
            ''
          );
          
          // Pattern 2: Remove sections that contain "Gráficas" or similar headers with empty content after JSON removal
          processedHtml = processedHtml.replace(
            /<(div|section)[^>]*>(\s*<h[1-6][^>]*>[^<]*[Gg]ráficas?[^<]*<\/h[1-6]>\s*)*<\/(div|section)>/gi,
            ''
          );
          
          // Pattern 3: Remove empty pre/code blocks
          processedHtml = processedHtml.replace(/<pre[^>]*>\s*<code[^>]*>\s*<\/code>\s*<\/pre>/gi, '');
          
          // Pattern 4: Remove markdown json code blocks
          processedHtml = processedHtml.replace(/```json[\s\S]*?```/gi, '');
        }
        
        // Then write to iframe
        if (iframeRef.current) {
          const iframe = iframeRef.current;
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (doc) {
            doc.open();
            doc.write(processedHtml);
            doc.close();
          }
        }
      }
    }, [html, processHtml]);
    
    // Render charts separately below the iframe if any were extracted
    return (
      <div>
        <iframe
          ref={iframeRef}
          style={{
            width: '100%',
            minHeight: '700px',
            border: 'none',
            background: 'white',
            borderRadius: '8px'
          }}
          title="HTML Report"
        />
        {extractedCharts.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-white flex items-center gap-2">
              <span>📊</span> Visualizaciones Interactivas
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {extractedCharts.map((chart, index) => (
                <ChartComponent key={index} chartData={chart} />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Simple HTML renderer component - always renders as HTML
  const HtmlRenderer = ({ html }) => {
    // Use an iframe for full HTML documents to properly isolate styles
    const isFullHtml = html && (html.includes('<!DOCTYPE') || html.includes('<html'));
    
    if (isFullHtml) {
      // For full HTML documents, use an iframe
      return (
        <iframe
          srcDoc={html}
          style={{
            width: '100%',
            minHeight: '600px',
            border: 'none',
            background: 'white',
            borderRadius: '8px'
          }}
          title="HTML Report"
        />
      );
    }

    const cleanedHtml = cleanHtmlFromCodeBlocks(html || '');

    return (
      <div 
        className="html-content"
        dangerouslySetInnerHTML={{ __html: cleanedHtml }}
      />
    );
  };

  // Use thread?.id (stable string) instead of thread (object ref recreated by parent every render)
  // This prevents the useEffect from firing on every parent re-render which previously caused
  // React Error #185 (Maximum update depth exceeded) when typing in followUp input while parent
  // re-rendered every 5s via refetchInterval on threads/insights queries.
  React.useEffect(() => {
    if (open && thread) {
      // Check if thread already has structured_report (insights case)
      if (thread.structured_report) {
        setReport(thread.structured_report);
        // Initialize conversation history from thread if available
        setConversationHistory(thread.conversation_history || []);
        setLoading(false);
      } else {
        loadReport();
        loadConversationHistory();
      }
    }
  }, [open, thread?.id]);

  const loadReport = async () => {
    try {
      setLoading(true);
      console.log('Loading report for thread:', thread.id);
      console.log('URL:', `${API}/api/companies/${thread.company_id}/threads/${thread.id}/report`);
      const response = await axios.get(
        `${API}/api/companies/${thread.company_id}/threads/${thread.id}/report`
      );
      console.log('Report loaded:', response.data);
      setReport(response.data);
    } catch (error) {
      console.error('Error loading report:', error);
      console.error('Error details:', error.response?.data);
    } finally {
      setLoading(false);
    }
  };

  const loadConversationHistory = async () => {
    try {
      const response = await axios.get(
        `${API}/api/companies/${thread.company_id}/threads/${thread.id}/messages`
      );
      setConversationHistory(response.data.conversation_history || []);
    } catch (error) {
      console.error('Error loading conversation history:', error);
    }
  };

  const handleSendFollowUp = async () => {
    if (!followUpMessage.trim()) return;
    
    setSendingMessage(true);
    const userMessageText = followUpMessage;
    setFollowUpMessage(''); // Clear input immediately for better UX
    
    try {
      let response;
      
      // Check if this is an insight or a thread
      const insightSourceTypes = ['agent00', 'agent_execution', 'module', 'governance', 'agent', 'premium', 'c_level', 'functional'];
      const isInsight = insightSourceTypes.includes(thread.source_type) || thread.source_type?.startsWith('agent');
      
      if (isInsight) {
        // Add temporary user message immediately for better UX
        const tempUserMessage = {
          id: 'temp-' + Date.now(),
          sender: 'user',
          content: userMessageText,
          timestamp: new Date().toISOString()
        };
        const tempAiMessage = {
          id: 'temp-ai-' + Date.now(),
          sender: 'assistant',
          content: '⏳ Generando respuesta...',
          status: 'generating',
          timestamp: new Date().toISOString()
        };
        setConversationHistory(prev => [...prev, tempUserMessage, tempAiMessage]);
        
        try {
          response = await axios.post(
            `${API}/api/companies/${thread.company_id}/insights/${thread.id}/follow-up`,
            { message: userMessageText },
            { timeout: 95000 } // Set timeout just under Cloudflare's 100s limit
          );
          
          // Check if response is being generated in background
          if (response.data.status === 'generating') {
            // Poll for completion
            const messageId = response.data.ai_message.id;
            pollForResponse(messageId, thread.company_id, thread.id);
            return;
          }
          
          // Legacy: Update conversation history with both user and AI messages
          if (response.data.conversation_history) {
            setConversationHistory(response.data.conversation_history);
          }
          setSendingMessage(false);
          toast.success('Respuesta generada exitosamente');
          
        } catch (error) {
          console.log('Follow-up error:', error.message, error.code);
          // If we get any timeout or network error, the backend might still be processing
          // Start polling to check if response was generated
          const isTimeout = error.code === 'ECONNABORTED' || 
                           error.message?.includes('timeout') ||
                           error.message?.includes('524') ||
                           error.message?.includes('Network Error') ||
                           !error.response;
          
          if (isTimeout) {
            console.log('Timeout/network error occurred, starting polling...');
            toast.info('Generando respuesta... Por favor espera unos segundos.');
            pollForInsightUpdate(thread.company_id, thread.id);
            return;
          }
          throw error; // Re-throw other errors
        }
      } else {
        // It's a regular thread, use the thread messages endpoint
        // Add temporary messages for UX
        const tempUserMsg = {
          id: 'temp-' + Date.now(),
          user_message: userMessageText,
          ai_response: '',
          status: 'generating',
          timestamp: new Date().toISOString()
        };
        setConversationHistory(prev => [...prev, tempUserMsg]);

        try {
          response = await axios.post(
            `${API}/api/companies/${thread.company_id}/threads/${thread.id}/messages`,
            { message: userMessageText, iad_codes: followUpIads.length ? followUpIads : null },
            { timeout: 95000 }
          );
          setFollowUpIads([]);

          if (response.data.status === 'generating') {
            const msgId = response.data.message_id;
            // Poll thread message status
            let attempts = 0;
            const poll = setInterval(async () => {
              attempts++;
              try {
                const s = await axios.get(
                  `${API}/api/companies/${thread.company_id}/threads/${thread.id}/message-status/${msgId}`
                );
                if (s.data.is_ready) {
                  clearInterval(poll);
                  // Reload full conversation
                  const full = await axios.get(
                    `${API}/api/companies/${thread.company_id}/threads/${thread.id}/messages`
                  );
                  setConversationHistory(full.data.conversation_history || []);
                  setSendingMessage(false);
                  toast.success('Respuesta generada exitosamente');
                  return;
                }
              } catch (e) {
                console.warn('Polling follow-up status failed', e);
              }
              if (attempts >= 40) {
                clearInterval(poll);
                setSendingMessage(false);
                toast.error('Tiempo de espera agotado. Recarga la página.');
              }
            }, 3000);
            return;
          }

          setConversationHistory([...conversationHistory, response.data]);
          setSendingMessage(false);
          toast.success('Respuesta generada exitosamente');
        } catch (error) {
          const isTimeout = error.code === 'ECONNABORTED' || error.message?.includes('timeout') || !error.response;
          if (isTimeout) {
            toast.info('Generando respuesta... Por favor espera.');
            // Poll thread messages for new completed entry
            let attempts = 0;
            const initLen = conversationHistory.length;
            const poll = setInterval(async () => {
              attempts++;
              try {
                const r = await axios.get(
                  `${API}/api/companies/${thread.company_id}/threads/${thread.id}/messages`
                );
                const hist = r.data.conversation_history || [];
                if (hist.length > initLen && hist[hist.length - 1]?.status !== 'generating') {
                  clearInterval(poll);
                  setConversationHistory(hist);
                  setSendingMessage(false);
                  toast.success('Respuesta generada exitosamente');
                }
              } catch (e) {
                if (attempts >= 40) { clearInterval(poll); setSendingMessage(false); }
              }
            }, 3000);
            return;
          }
          throw error;
        }
      }
      
    } catch (error) {
      console.error('Error sending follow-up message:', error);
      // Don't show error for timeouts - we're polling
      if (error.response?.status !== 524 && error.code !== 'ECONNABORTED') {
        toast.error(error.response?.data?.detail || 'Error al enviar mensaje de seguimiento');
      }
      setSendingMessage(false);
    }
  };

  // Poll for response by message ID
  const pollForResponse = (messageId, companyId, insightId) => {
    let attempts = 0;
    const maxAttempts = 40; // 2 minutes (40 * 3s)
    
    const pollInterval = setInterval(async () => {
      attempts++;
      try {
        const statusResponse = await axios.get(
          `${API}/api/companies/${companyId}/insights/${insightId}/follow-up-status/${messageId}`
        );
        
        if (statusResponse.data.is_ready) {
          clearInterval(pollInterval);
          setConversationHistory(prev => prev.map(msg => 
            msg.id === messageId || msg.id?.startsWith('temp-ai-')
              ? { ...msg, id: messageId, content: statusResponse.data.content, status: 'completed' }
              : msg
          ));
          setSendingMessage(false);
          toast.success('Respuesta generada exitosamente');
        }
      } catch (pollError) {
        console.error('Error polling status:', pollError);
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setSendingMessage(false);
          toast.error('Tiempo de espera agotado. Recarga la página para ver la respuesta.');
        }
      }
    }, 3000);
  };

  // Poll for insight update (when we don't have messageId)
  const pollForInsightUpdate = (companyId, insightId) => {
    let attempts = 0;
    const maxAttempts = 40;
    const initialHistoryLength = conversationHistory.length;
    
    const pollInterval = setInterval(async () => {
      attempts++;
      try {
        const response = await axios.get(
          `${API}/api/companies/${companyId}/insights/${insightId}`
        );
        
        const newHistory = response.data.conversation_history || [];
        
        // Check if we have new messages
        if (newHistory.length > initialHistoryLength) {
          const lastMessage = newHistory[newHistory.length - 1];
          // Check if the last AI message is completed
          if (lastMessage.sender === 'assistant' && lastMessage.status !== 'generating') {
            clearInterval(pollInterval);
            setConversationHistory(newHistory);
            setSendingMessage(false);
            toast.success('Respuesta generada exitosamente');
            return;
          }
        }
        
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setSendingMessage(false);
          toast.info('La respuesta puede tardar. Recarga la página en unos segundos.');
        }
      } catch (pollError) {
        console.error('Error polling insight:', pollError);
        if (attempts >= maxAttempts) {
          clearInterval(pollInterval);
          setSendingMessage(false);
        }
      }
    }, 3000);
  };

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);
      
      // Determine the correct endpoint based on whether this is an insight or thread
      const insightSourceTypes = ['agent00', 'agent_execution', 'module', 'governance', 'agent', 'premium', 'c_level', 'functional'];
      const isInsight = insightSourceTypes.includes(thread.source_type) || thread.source_type?.startsWith('agent');
      
      const endpoint = isInsight
        ? `${API}/api/companies/${thread.company_id}/insights/${thread.id}/pdf`
        : `${API}/api/companies/${thread.company_id}/threads/${thread.id}/report/pdf`;
      
      const response = await axios.get(endpoint, { responseType: 'blob' });
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `reporte_${thread.id}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado exitosamente');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error(error.response?.data?.detail || 'Error al descargar PDF');
    } finally {
      setDownloading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return <CheckCircle className="w-4 h-4 text-green-400" />;
      case 'warning':
        return <AlertCircle className="w-4 h-4 text-yellow-400" />;
      case 'danger':
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-400" />;
      default:
        return <Minus className="w-4 h-4 text-blue-400" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'success':
        return 'bg-green-500/20 text-green-300 border-green-500/30';
      case 'warning':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'danger':
      case 'error':
        return 'bg-red-500/20 text-red-300 border-red-500/30';
      default:
        return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
    }
  };

  const getThreadTypeLabel = (type) => {
    const labels = {
      'analysis': 'Análisis',
      'prediction': 'Predicción',
      'projection': 'Proyección'
    };
    return labels[type] || type;
  };

  // Function to find all chart JSON objects in text
  const findChartJsonObjects = (text) => {
    if (!text) return [];
    
    const charts = [];
    const textStr = String(text);
    
    console.log('[findChartJsonObjects] Analyzing text length:', textStr.length);
    
    // Pattern 1: Find JSON objects that look like charts
    const regex = /\{[^{}]*"type"\s*:\s*"(bar|line|pie)"[^{}]*"data"\s*:\s*\[[^\]]*\][^{}]*\}/gi;
    
    let match;
    while ((match = regex.exec(textStr)) !== null) {
      try {
        const jsonStr = match[0];
        const chartData = JSON.parse(jsonStr);
        if (chartData.type && chartData.data && Array.isArray(chartData.data)) {
          charts.push(chartData);
        }
      } catch (e) {}
    }
    
    // Pattern 2: Find charts in code blocks
    const codeBlockRegex = /```json\s*([\s\S]*?)```/gi;
    let codeMatch;
    while ((codeMatch = codeBlockRegex.exec(textStr)) !== null) {
      try {
        const jsonContent = codeMatch[1].trim();
        const chartData = JSON.parse(jsonContent);
        if (chartData.type && chartData.data && ['bar', 'line', 'pie'].includes(chartData.type)) {
          charts.push(chartData);
        }
      } catch (e) {}
    }
    
    // Pattern 3: SIMPLE KPI Detection - Look for "Valor actual" and "Benchmark" values
    // Match: "Valor actual: 146.6" or "Valor actual: 146.6%" 
    const valorActualMatch = textStr.match(/Valor\s+actual[:\s]+(\d+(?:[.,]\d+)?)/i);
    const benchmarkMatch = textStr.match(/Benchmark[^:]*[:\s]+(\d+(?:[.,]\d+)?)/i);
    
    console.log('[findChartJsonObjects] valorActualMatch:', valorActualMatch?.[1]);
    console.log('[findChartJsonObjects] benchmarkMatch:', benchmarkMatch?.[1]);
    
    if (valorActualMatch && benchmarkMatch) {
      const valorActual = parseFloat(valorActualMatch[1].replace(',', '.'));
      const benchmark = parseFloat(benchmarkMatch[1].replace(',', '.'));
      
      if (!isNaN(valorActual) && !isNaN(benchmark)) {
        charts.push({
          type: 'bar',
          title: 'Comparación: KPI Actual vs Benchmark',
          data: [
            { name: 'Valor Actual', value: valorActual },
            { name: 'Benchmark Industria', value: benchmark }
          ]
        });
        console.log('[findChartJsonObjects] Created KPI comparison chart:', valorActual, 'vs', benchmark);
      }
    }
    
    // Pattern 4: Alternative - Look for any "Tasa de Conversión" or similar KPI patterns
    const tasaMatch = textStr.match(/Tasa\s+de\s+Conversión[,:\s]+(\d+(?:[.,]\d+)?)/i);
    if (tasaMatch && !charts.length) {
      const tasaValue = parseFloat(tasaMatch[1].replace(',', '.'));
      // Try to find a benchmark nearby
      const benchNearby = textStr.match(/(?:benchmark|meta|industria|estándar)[^0-9]*(\d+(?:[.,]\d+)?)/i);
      if (benchNearby && !isNaN(tasaValue)) {
        const benchValue = parseFloat(benchNearby[1].replace(',', '.'));
        charts.push({
          type: 'bar',
          title: 'Tasa de Conversión vs Benchmark',
          data: [
            { name: 'Tasa de Conversión', value: tasaValue },
            { name: 'Benchmark', value: benchValue }
          ]
        });
        console.log('[findChartJsonObjects] Created Tasa comparison chart');
      }
    }
    
    // Pattern 5: Look for percentages in format "X% ... Y%"
    const percentages = textStr.match(/(\d+(?:[.,]\d+)?)\s*%/g);
    if (percentages && percentages.length >= 2 && !charts.length) {
      // Check if context suggests KPI comparison
      if (textStr.toLowerCase().includes('kpi') || textStr.toLowerCase().includes('benchmark')) {
        const values = percentages.slice(0, 2).map(p => parseFloat(p.replace(',', '.').replace('%', '')));
        if (values.every(v => !isNaN(v))) {
          charts.push({
            type: 'bar',
            title: 'Comparación de Valores',
            data: [
              { name: 'Valor Actual', value: values[0] },
              { name: 'Benchmark', value: values[1] }
            ]
          });
          console.log('[findChartJsonObjects] Created percentage comparison chart');
        }
      }
    }
    
    console.log('[findChartJsonObjects] Total charts found:', charts.length);
    return charts;
  };

  // Memoized component to render content with embedded charts
  const ContentWithCharts = ({ content, isHtml = false, charts = [] }) => {
    // Auto-detect HTML if not explicitly set
    const shouldRenderAsHtml = isHtml || isHTML(content);
    
    // Process content without chart detection to avoid infinite loops
    const cleanContent = useMemo(() => {
      if (!content) return '';
      
      let cleaned = String(content);
      
      // Clean markdown code block wrappers (```html ... ```)
      cleaned = cleanHtmlFromCodeBlocks(cleaned);
      
      // Remove ```json ... ``` blocks containing chart data
      cleaned = cleaned.replace(/<!--\s*CHART_START\s*-->[\s\S]*?<!--\s*CHART_END\s*-->/gi, '');
      cleaned = cleaned.replace(/```json\s*\{[\s\S]*?"type"\s*:\s*"(?:bar|line|pie)"[\s\S]*?\}\s*```/gi, '');
      
      // Remove standalone chart JSON objects
      const chartJsonRegex = /\{[^{}]*"type"\s*:\s*"(bar|line|pie)"[^{}]*"data"\s*:\s*\[[^\]]*\][^{}]*\}/gi;
      cleaned = cleaned.replace(chartJsonRegex, '');
      
      // Clean up multiple newlines
      cleaned = cleaned.replace(/\n{3,}/g, '\n\n').trim();
      
      return cleaned;
    }, [content]);
    
    if (!content) return null;
    
    return (
      <>
        {/* Render the text content */}
        {shouldRenderAsHtml ? (
          <div 
            dangerouslySetInnerHTML={{ __html: extractBodyContent(cleanContent) }}
            className="html-content"
          />
        ) : (
          <ReactMarkdown 
            rehypePlugins={[rehypeRaw]}
            components={{
              h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-white mt-6 mb-4" {...props} />,
              h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-blue-300 mt-4 mb-3" {...props} />,
              p: ({node, ...props}) => <p className="mb-3 text-slate-300" {...props} />,
              ul: ({node, ...props}) => <ul className="list-disc list-inside space-y-2 my-4 text-slate-300" {...props} />,
              li: ({node, ...props}) => <li className="text-slate-300" {...props} />,
              strong: ({node, ...props}) => <strong className="font-bold text-white" {...props} />,
              code: ({node, inline, ...props}) => {
                if (inline) {
                  return <code className="bg-slate-800 px-1 rounded text-blue-300" {...props} />;
                }
                const codeContent = props.children?.toString() || '';
                if (codeContent.includes('"type"') && codeContent.includes('"data"')) {
                  return null; // Skip chart JSON code blocks
                }
                return <code className="block bg-slate-800 p-2 rounded my-2 text-sm overflow-x-auto" {...props} />;
              }
            }}
          >
            {cleanContent}
          </ReactMarkdown>
        )}
        
        {/* Render charts from structured data */}
        {charts && charts.length > 0 && (
          <div className="mt-6 space-y-4">
            <h3 className="text-lg font-semibold text-blue-300 flex items-center gap-2">
              📊 Visualizaciones
            </h3>
            {charts.map((chart, idx) => (
              <ChartComponent key={`chart-${idx}`} chartData={chart} />
            ))}
          </div>
        )}
      </>
    );
  };

  // Function to render charts (legacy, used by conversation history)
  const renderChart = (chartData) => {
    if (!chartData) return null;

    let { type, title, data, labels, keys, currency } = chartData;

    // Transform format if data is array of numbers with separate labels array
    if (labels && Array.isArray(labels) && Array.isArray(data) && typeof data[0] === 'number') {
      data = labels.map((label, idx) => ({
        name: label,
        value: data[idx]
      }));
    }

    if (!data || !Array.isArray(data)) return null;

    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899'];

    switch (type) {
      case 'line':
        return (
          <div className="mt-4 bg-slate-800/30 p-4 rounded-lg border border-slate-700">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">{title}</h4>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
                <Legend />
                {keys ? (
                  Object.keys(keys).map((key, idx) => (
                    <Line
                      key={key}
                      type="monotone"
                      dataKey={key}
                      name={keys[key]}
                      stroke={COLORS[idx % COLORS.length]}
                      strokeWidth={2}
                      dot={{ fill: COLORS[idx % COLORS.length], r: 4 }}
                    />
                  ))
                ) : (
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={COLORS[0]}
                    strokeWidth={2}
                    dot={{ fill: COLORS[0], r: 4 }}
                  />
                )}
              </LineChart>
            </ResponsiveContainer>
          </div>
        );

      case 'bar':
        return (
          <div className="mt-4 bg-slate-800/30 p-4 rounded-lg border border-slate-700">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">{title}</h4>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="name" stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <YAxis stroke="#94a3b8" style={{ fontSize: '12px' }} />
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                  labelStyle={{ color: '#e2e8f0' }}
                />
                <Legend />
                {keys ? (
                  Object.keys(keys).map((key, idx) => (
                    <Bar key={key} dataKey={key} name={keys[key]} fill={COLORS[idx % COLORS.length]} />
                  ))
                ) : (
                  <Bar dataKey="value" fill={COLORS[0]} />
                )}
              </BarChart>
            </ResponsiveContainer>
          </div>
        );

      case 'pie':
        return (
          <div className="mt-4 bg-slate-800/30 p-4 rounded-lg border border-slate-700">
            <h4 className="text-sm font-semibold text-slate-300 mb-3">{title}</h4>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={100}
                  label={({ name, value }) => `${name}: ${currency ? `${currency}${value.toLocaleString()}` : value}`}
                >
                  {data.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #475569' }}
                  formatter={(value) => currency ? `${currency}${value.toLocaleString()}` : value}
                />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        );

      default:
        return null;
    }
  };

  // Early return if thread is null
  if (!thread) {
    return null;
  }

  const isInsight = thread.structured_report && thread.structured_report.report_format === 'executive_insight';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-dark border-blue-500/20 text-white w-[95vw] max-w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0">
        {/* Compact Fixed Header */}
        <DialogHeader className="px-4 py-3 border-b border-slate-700 flex-shrink-0">
          <VisuallyHidden>
            <DialogDescription>
              Visualización del reporte de análisis estratégico
            </DialogDescription>
          </VisuallyHidden>
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                {!isInsight && thread.thread_type && (
                  <Badge className="badge-info text-xs">
                    {getThreadTypeLabel(thread.thread_type)}
                  </Badge>
                )}
                {isInsight && (
                  <Badge className="badge-warning text-xs">
                    <Lightbulb className="w-3 h-3 mr-1" />
                    Insight
                  </Badge>
                )}
                <Badge className="badge-success text-xs">
                  <Clock className="w-3 h-3 mr-1" />
                  {new Date(thread.created_at).toLocaleDateString()}
                </Badge>
                <DialogTitle className="text-lg font-semibold truncate">
                  {report?.title || 'Reporte de Análisis'}
                </DialogTitle>
              </div>
            </div>
            {!isInsight && (
              <Button
                onClick={handleDownloadPDF}
                disabled={downloading || !report}
                size="sm"
                className="btn-gradient flex-shrink-0 mr-8"
              >
                <Download className="w-4 h-4 mr-1" />
                Descargar PDF
              </Button>
            )}
          </div>
          
          {/* Compact Query Display */}
          <p className="text-xs text-slate-400 mt-2 line-clamp-2">
            <span className="font-semibold text-blue-300">Consulta:</span> {thread.query}
          </p>
        </DialogHeader>

        {/* Scrollable Content */}
        <div className="overflow-y-auto flex-1 p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                <p className="text-slate-400">Cargando reporte...</p>
              </div>
            </div>
          ) : report ? (
            <>
              {/* Executive Summary */}
              <Card className="glass-dark border-blue-500/20">
                <CardHeader>
                  <CardTitle className="text-white flex items-center gap-2">
                    <Lightbulb className="w-5 h-5 text-yellow-400" />
                    {report.title || report.agent_name || 'Reporte Ejecutivo'}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {(() => {
                    // Use executive_summary from report, or fall back to thread.content
                    const contentToRender = report.executive_summary || thread?.content || '';
                    
                    // Check if content is HTML (full document OR styled HTML fragment)
                    const trimmedContent = contentToRender.trim();
                    const isFullHtmlDoc = contentToRender && (
                      trimmedContent.startsWith('<!DOCTYPE') || 
                      trimmedContent.startsWith('<html') ||
                      // Also detect HTML fragments with inline styles (common in agent outputs)
                      trimmedContent.startsWith('<div style=') ||
                      trimmedContent.startsWith('<div class=') ||
                      // Check for substantial styled HTML content
                      (trimmedContent.startsWith('<div') && trimmedContent.includes('style='))
                    );
                    
                    // Get charts from report or thread
                    const chartsToRender = report.charts || thread?.charts || [];
                    
                    if (isFullHtmlDoc) {
                      // Use a component that handles iframe content properly
                      return (
                        <>
                          <IframeHtmlRenderer html={contentToRender} />
                          {/* Always render charts from report separately */}
                          {chartsToRender.length > 0 && (
                            <div className="mt-6 space-y-4">
                              <h3 className="text-lg font-semibold text-blue-300 flex items-center gap-2">
                                📊 Gráficas de Rendimiento
                              </h3>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {chartsToRender.map((chart, idx) => (
                                  <ChartComponent key={`report-chart-${idx}`} chartData={chart} />
                                ))}
                              </div>
                            </div>
                          )}
                        </>
                      );
                    }
                    
                    // Check if content is HTML (but not full document)
                    const contentIsHtml = report.is_html === true || isHTML(contentToRender);
                    
                    if (contentIsHtml) {
                      return (
                        <div className="text-slate-300 leading-relaxed">
                          <HtmlRenderer html={contentToRender} />
                          {/* Always render charts from report separately */}
                          {chartsToRender.length > 0 && (
                            <div className="mt-6 space-y-4">
                              <h3 className="text-lg font-semibold text-blue-300 flex items-center gap-2">
                                📊 Gráficas de Rendimiento
                              </h3>
                              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                                {chartsToRender.map((chart, idx) => (
                                  <ChartComponent key={`report-chart-${idx}`} chartData={chart} />
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    } else {
                      return (
                        <div className="text-slate-300 leading-relaxed">
                          <ContentWithCharts 
                            content={contentToRender} 
                            isHtml={false}
                            charts={report.charts || thread?.charts || []}
                          />
                        </div>
                      );
                    }
                  })()}
                </CardContent>
              </Card>

              <IADSustento analysis={report.iad_analysis || thread.iad_analysis} />

              {/* Critical Situation */}
              {report.critical_situation && (
                <Card className="glass-dark border-red-500/30 bg-red-500/5">
                  <CardHeader>
                    <CardTitle className="text-red-300 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5" />
                      Situación Crítica
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {report.critical_situation.description && (
                      <p className="text-slate-300 mb-4">
                        {report.critical_situation.description}
                      </p>
                    )}
                    
                    {report.critical_situation.metrics && report.critical_situation.metrics.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {report.critical_situation.metrics.map((metric, idx) => (
                          <div 
                            key={idx}
                            className={`p-4 rounded-lg border ${getStatusColor(metric.status)}`}
                          >
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-xs font-medium uppercase tracking-wide">
                                {metric.label}
                              </span>
                              {getStatusIcon(metric.status)}
                            </div>
                            <div className="text-2xl font-bold">
                              {metric.value}
                              {metric.unit && (
                                <span className="text-sm ml-1">{metric.unit}</span>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Metrics Summary */}
              {report.metrics_summary && report.metrics_summary.length > 0 && (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  {report.metrics_summary.map((metric, idx) => (
                    <Card key={idx} className="glass-dark border-blue-500/20">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs text-slate-400 uppercase">
                            {metric.label}
                          </span>
                          {getStatusIcon(metric.status)}
                        </div>
                        <div className="text-xl font-bold text-white">
                          {metric.value}
                          {metric.unit && (
                            <span className="text-sm ml-1 text-slate-400">{metric.unit}</span>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}

              {/* Sections */}
              {report.sections && report.sections.map((section, idx) => (
                <Card key={idx} className="glass-dark border-blue-500/20">
                  <CardHeader>
                    <CardTitle className="text-white">{section.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 leading-relaxed whitespace-pre-line mb-4">
                      {section.content}
                    </p>
                    
                    {section.metrics && section.metrics.length > 0 && (
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                        {section.metrics.map((metric, midx) => (
                          <div 
                            key={midx}
                            className="p-3 bg-slate-800/50 rounded-lg border border-slate-700"
                          >
                            <div className="flex items-center justify-between">
                              <span className="text-sm text-slate-400">{metric.label}</span>
                              <span className="text-sm font-medium text-white">
                                {metric.value} {metric.unit}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}

              {/* Recommendation */}
              {report.recommendation && (
                <Card className="glass-dark border-green-500/30 bg-green-500/5">
                  <CardHeader>
                    <CardTitle className="text-green-300 flex items-center gap-2">
                      <Target className="w-5 h-5" />
                      {report.recommendation.title || 'Recomendación Principal'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-slate-300 mb-4">
                      {report.recommendation.content}
                    </p>
                    
                    {report.recommendation.action_items && report.recommendation.action_items.length > 0 && (
                      <div className="mt-4">
                        <p className="text-sm font-semibold text-green-300 mb-2">
                          Acciones Sugeridas:
                        </p>
                        <ul className="space-y-2">
                          {report.recommendation.action_items.map((action, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-slate-300">
                              <CheckCircle className="w-4 h-4 mt-1 text-green-400 flex-shrink-0" />
                              <span>{action}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Key Message */}
              {report.key_message && (
                <Card className="glass-dark border-blue-500/30 bg-blue-500/5">
                  <CardContent className="p-6">
                    <p className="text-lg font-medium text-blue-200 italic text-center">
                      "{report.key_message}"
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Footer - Agents Used */}
              <div className="border-t border-slate-700 pt-4">
                <p className="text-xs text-slate-400 mb-2">
                  <strong>Agentes Consultados:</strong>
                </p>
                <div className="flex flex-wrap gap-2">
                  {report.agents_used && report.agents_used.map((agent, idx) => (
                    <Badge key={idx} className="badge-secondary">
                      {agent}
                    </Badge>
                  ))}
                </div>
                <p className="text-xs text-slate-500 mt-4">
                  Generado por Vector Solutions • {new Date().toLocaleDateString()}
                </p>
              </div>

              {/* Follow-up Chat Section - Only for threads, not insights */}
              {!isInsight && (
                <Card className="glass-dark border-green-500/20 mt-6">
                  <CardHeader>
                    <CardTitle className="text-white flex items-center gap-2">
                      <MessageCircle className="w-5 h-5 text-green-400" />
                      Preguntas de Seguimiento
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                  {/* Conversation History */}
                  {conversationHistory.length > 0 && (
                    <div className="space-y-4 max-h-96 overflow-y-auto mb-4">
                      {conversationHistory.map((msg, idx) => (
                        <div key={msg.id || msg.message_id || idx}>
                          {msg.sender === 'user' ? (
                            /* User Message */
                            <div className="flex justify-end mb-2">
                              <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3 max-w-[80%]">
                                <p className="text-sm text-blue-100">{msg.content || msg.user_message}</p>
                                <p className="text-xs text-slate-500 mt-1">
                                  {new Date(msg.timestamp).toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                          ) : msg.sender === 'assistant' ? (
                            /* AI Response */
                            <div className="flex justify-start mb-2">
                              <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 max-w-[85%]">
                                <div className="text-slate-300 chat-response-content" style={{ fontSize: '15px', lineHeight: '1.6' }}>
                                  <ContentWithCharts 
                                    content={msg.content || msg.ai_response} 
                                    isHtml={isHTML(msg.content || msg.ai_response)}
                                    charts={msg.charts || []}
                                  />
                                  
                                  {/* Render chart if available as separate field (legacy) */}
                                  {msg.chart_data && (
                                    <ChartComponent chartData={msg.chart_data} />
                                  )}
                                </div>
                                <p className="text-xs text-slate-500 mt-2">
                                  {new Date(msg.timestamp).toLocaleTimeString()}
                                </p>
                              </div>
                            </div>
                          ) : (
                            /* Old format fallback (for regular threads) */
                            <div className="space-y-2">
                              <div className="flex justify-end">
                                <div className="bg-blue-500/20 border border-blue-500/30 rounded-lg p-3 max-w-[80%]">
                                  <p className="text-sm text-blue-100">{msg.user_message}</p>
                                  <p className="text-xs text-slate-500 mt-1">
                                    {new Date(msg.timestamp).toLocaleTimeString()}
                                  </p>
                                </div>
                              </div>
                              {msg.status === 'generating' ? (
                                <div className="flex justify-start">
                                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 max-w-[85%]">
                                    <div className="flex items-center gap-2 text-slate-400">
                                      <div className="animate-spin h-4 w-4 border-2 border-blue-400 border-t-transparent rounded-full"></div>
                                      <span className="text-sm">Generando respuesta...</span>
                                    </div>
                                  </div>
                                </div>
                              ) : msg.ai_response ? (
                                <div className="flex justify-start">
                                  <div className="bg-slate-800/50 border border-slate-700 rounded-lg p-4 max-w-[85%]">
                                    <div className="text-slate-300 chat-response-content" style={{ fontSize: '15px', lineHeight: '1.6' }}>
                                      <ContentWithCharts 
                                        content={msg.ai_response} 
                                        isHtml={isHTML(msg.ai_response)}
                                        charts={msg.charts || []}
                                      />
                                      {msg.chart_data && !msg.charts?.length && (
                                        <ChartComponent chartData={msg.chart_data} />
                                      )}
                                    </div>
                                    {msg.iad_analysis && <div className="mt-3"><IADSustento analysis={msg.iad_analysis} compact /></div>}
                                  </div>
                                </div>
                              ) : null}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Input Section */}
                  <IADSelector
                    companyId={thread.company_id}
                    query={followUpMessage}
                    value={followUpIads}
                    onChange={setFollowUpIads}
                    testidPrefix="followup-iad-selector"
                  />
                  <div className="flex gap-2">
                    <Input
                      placeholder="Haz una pregunta de seguimiento sobre este análisis..."
                      value={followUpMessage}
                      onChange={(e) => setFollowUpMessage(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' && !sendingMessage) {
                          handleSendFollowUp();
                        }
                      }}
                      className="flex-1 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500"
                      disabled={sendingMessage}
                      data-testid="followup-input"
                    />
                    <Button
                      onClick={handleSendFollowUp}
                      disabled={sendingMessage || !followUpMessage.trim()}
                      className="btn-gradient"
                      data-testid="followup-send-btn"
                    >
                      {sendingMessage ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Enviando...
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4 mr-2" />
                          Enviar
                        </>
                      )}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              )}
            </>
          ) : (
            <div className="text-center py-12">
              <AlertCircle className="w-12 h-12 mx-auto mb-4 text-slate-500" />
              <p className="text-slate-400">No se pudo cargar el reporte</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ReportViewerModal;
